import { useContext, useEffect, useMemo, useState } from 'react';
import PubSub from 'pubsub-js';
import { useLocation } from 'react-router';
import { useAsyncFn } from 'react-use';
import useAsync, { AsyncState } from 'react-use/lib/useAsync';
import client from 'services/client';
import {
  AverageBallWeight,
  Buyer,
  CalculatedAverageBallWeightByMaterialId,
  ChangeLog,
  Characterization,
  CharacterizationFilters,
  Client,
  ClientContract,
  Closure,
  ClosureStatusEnum,
  ConditionedStock,
  DistributionKey,
  EstimatedReception,
  Event,
  Expedition,
  ExpeditionBuyerConfirmation,
  ExpeditionFilters,
  InboundStock,
  InformationMessage,
  InformationMessageType,
  InventoriedInboundStock,
  InventoriedOutboundStock,
  InventoriedOutboundStocksAgregateByMaterialId,
  Material,
  MaterialStandard,
  OrderFilter,
  Pagination,
  Production,
  ProductionFilters,
  ProvisionalOutboundStock,
  ProvisionalOutBoundStockQueryGroupByEnum,
  ProvisionalOutboundStocksAgregateByMaterialId,
  ProvisionalOutboundStocksIndexedByStringId,
  QueryParams,
  Reception,
  ReceptionFilters,
  SortingCenter,
  SortingCenterContract,
  SortingCenterContractsRequirements,
  SortingCenterContractStatus,
  SortingCenterContractStatusByClient,
  StockConditionneRepreneurByBuyerThenMaterial,
  StockConditionneRepreneurFilters,
  Stream,
  StreamFilters,
  User,
  Wastage,
} from 'types';
import { formatDateAsQueryParam, isValidDate } from 'utils/date';
import {
  getBuyersFromMaterialWithClientContracts,
  getClientsForGivenBuyerAndMaterialFromContracts,
  getClientsFromActiveContracts,
  getMaterialsFromClientContracts,
  getStreamsFromClientAndActiveContracts,
} from 'utils/entity';
import { useLatestClosure } from 'utils/closureHooks';
import { transformId, transformObjectId } from 'services/apiPlatformTransformer';
import { AppContext } from 'context';
import { getMaterialsById } from 'services/material';
import { isMaterialBulk } from 'utils/formValidation';
import { formatFilterAsQueryParams } from 'utils/filters';
import { DEFAULT_ITEMS_PER_SELECT } from './tableFilterContext';

export const useQueryParams = (): URLSearchParams => {
  const location = useLocation();
  return useMemo(() => new URLSearchParams(location.search), [location.search]);
};

/** @param sortingCenterId Fully qualified id, ex: /api/sorting_centers/87 */
export const useDistributionKeys = (
  sortingCenterId: string,
  selectedDate?: Date,
): [DistributionKey[] | null, number] => {
  sortingCenterId = sortingCenterId.split('/')[3];
  let params = {};

  if (selectedDate) {
    params = {
      month: String(selectedDate.getMonth() + 1),
      year: String(selectedDate.getFullYear()),
      sortingCenterId,
    };
  }
  const state = useFetchDataWithFilters(`/distribution_keys`, params, 1, 1000);

  if (!state.value || state.loading) {
    return [null, -1];
  }

  return [state.value.results, state.value.total];
};

export const useProvisionalOutboundStocksIndexedByClientId = (
  clients: Client[],
  endDate?: Date | null,
  materialId?: string,
  overrideResults?: ProvisionalOutboundStocksIndexedByStringId,
): ProvisionalOutboundStocksIndexedByStringId | null => {
  const readyToFetch = !!endDate && !!materialId && !overrideResults;
  const params: {
    materialId?: string;
    endDate: string;
    groupBy: ProvisionalOutBoundStockQueryGroupByEnum;
    selectedBuyerId?: string;
  } = {
    endDate: endDate ? formatDateAsQueryParam(endDate) : '',
    materialId,
    groupBy: ProvisionalOutBoundStockQueryGroupByEnum.CLIENT,
  };

  const state = useFetchDataWithFilters(`/provisional_outbound_stocks`, params, 1, 50, readyToFetch);

  const provisionalOutboundStocksIndexedByClientId = state.value?.results?.[0] as
    | undefined
    | ProvisionalOutboundStocksIndexedByStringId;

  const filteredStocks = useMemo(() => {
    if (!provisionalOutboundStocksIndexedByClientId || state.loading) return null;

    const clientIds = clients.map(c => transformObjectId(c));
    return Object.fromEntries(
      Object.entries(provisionalOutboundStocksIndexedByClientId).filter(([key]: [string, ProvisionalOutboundStock]) =>
        clientIds.includes(key),
      ),
    );
  }, [state.loading, clients, provisionalOutboundStocksIndexedByClientId]);

  if (overrideResults) {
    return overrideResults;
  }

  if (!materialId || !filteredStocks || Object.keys(filteredStocks).length === 0) {
    return null;
  }

  return filteredStocks;
};

/**
 * ConditionedStock = Provisional outbound stock + Material infos + Inventoried outbound stock + Average ball weight
 */
export const useConditionedStocks = (year: string, month: string): ConditionedStock[] => {
  const conditionedStocks = [];
  const currentEndDate = new Date(parseInt(year), parseInt(month), 0);
  const materials = useMaterials({ itemsPerPage: String(DEFAULT_ITEMS_PER_SELECT) });
  const materialsById = getMaterialsById(materials);
  const [averageBallWeights] = useAverageBallWeightByMaterialId(year, month);
  const [inventoriedOutboundStocks, loadingInventoriedOutboundStocks] = useInventoriedOutboundStocks(year, month);
  const [provisionalOutboundStocksAgregateByMaterial, loadingProvisionalOutboundStocksAgregateByMaterialId]: [
    ProvisionalOutboundStocksAgregateByMaterialId,
    boolean,
  ] = useProvisionalOutboundStocksAgregateByMaterialId(currentEndDate);

  if (
    !loadingProvisionalOutboundStocksAgregateByMaterialId &&
    !loadingInventoriedOutboundStocks &&
    materialsById &&
    averageBallWeights
  ) {
    const inventoriedOutboundStocksByMaterialId: InventoriedOutboundStocksAgregateByMaterialId =
      inventoriedOutboundStocks?.reduce<InventoriedOutboundStocksAgregateByMaterialId>(
        (inventoriedOutboundStocksByMaterialId, inventoriedOutboundStock) => {
          inventoriedOutboundStocksByMaterialId[inventoriedOutboundStock.material] = inventoriedOutboundStock;
          return inventoriedOutboundStocksByMaterialId;
        },
        {},
      );

    for (const provisionalOutboundStock of Object.values(provisionalOutboundStocksAgregateByMaterial)) {
      const materialUri = provisionalOutboundStock.material;
      const materialId = transformId(materialUri);
      const calculatedAverageBallWeightInKilos = averageBallWeights[parseInt(materialId)] ?? 0;

      // conditionedStock = Provisional outbound stock + Material infos + Inventoried outbound stock + Average ball weight
      const conditionedStock: ConditionedStock = {
        sortingCenter: provisionalOutboundStock.sortingCenter,
        client: provisionalOutboundStock.client,
        weightInKilos: provisionalOutboundStock.weight,
        conditioningQuantity: provisionalOutboundStock.conditioningQuantity ?? 0,
        material: provisionalOutboundStock.material,
        materialName: materialsById[parseInt(materialId)]?.shortName ?? '',
        materialObject: materialsById[parseInt(materialId)] ?? null,
        isMaterialBulk: isMaterialBulk(materialsById[parseInt(materialId)]),
        inventoryId: inventoriedOutboundStocksByMaterialId?.[materialUri]?.id ?? 0,
        inventoryIdApiPlatform: inventoriedOutboundStocksByMaterialId?.[materialUri]?.['@id'],
        inventoryMonth: inventoriedOutboundStocksByMaterialId?.[materialUri]?.month,
        inventoryYear: inventoriedOutboundStocksByMaterialId?.[materialUri]?.year,
        inventoryConditioningQuantity: inventoriedOutboundStocksByMaterialId?.[materialUri]?.conditioningQuantity ?? 0,
        inventoryCalculatedAverageBallWeightInKilos:
          inventoriedOutboundStocksByMaterialId?.[materialUri]?.calculatedAverageBallWeight,
        inventoryWeight: inventoriedOutboundStocksByMaterialId?.[materialUri]?.weight
          ? inventoriedOutboundStocksByMaterialId?.[materialUri]?.weight
          : 0,
        inventoryAverageBallWeight:
          inventoriedOutboundStocksByMaterialId?.[materialUri]?.averageWeight ?? calculatedAverageBallWeightInKilos,
        calculatedAverageBallWeightInKilos,
      };

      conditionedStocks.push(conditionedStock);
    }
  }

  return conditionedStocks;
};

export const useProvisionalOutboundStocksAgregateByMaterialId = (
  endDate?: Date,
): [ProvisionalOutboundStocksAgregateByMaterialId, boolean] => {
  const readyToFetch = !!endDate;
  const params: { endDate: string; groupBy: ProvisionalOutBoundStockQueryGroupByEnum } = {
    endDate: endDate ? formatDateAsQueryParam(endDate) : '',
    groupBy: ProvisionalOutBoundStockQueryGroupByEnum.MATERIAL,
  };

  const state = useFetchDataWithFilters(`/provisional_outbound_stocks`, params, 1, 50, readyToFetch);

  if (!state.value?.results || state.loading) {
    return [{}, true];
  }

  const provisionalOutboundStocksAgregateByMaterialId = state.value?.results;

  return [provisionalOutboundStocksAgregateByMaterialId, false];
};

export const useInventoriedOutboundStocks = (year?: string, month?: string): [InventoriedOutboundStock[], boolean] => {
  let params: QueryParams = {};

  if (year) params['year'] = year;
  if (month) params['month'] = month;

  const state = useFetchDataWithFilters('/inventoried_outbound_stocks', params);

  if (state.loading || !state.value) {
    return [[], true];
  }

  return [state.value.results, false];
};

export const useActiveContractToDate = (
  date: Date | null,
  readyToFetchData?: boolean,
): SortingCenterContract[] | null => {
  let params: QueryParams = {};

  if (date && isValidDate(date)) {
    params['endDate[after]'] = date.toDateString();
    params['startDate[before]'] = date.toDateString();
  }

  const state = useFetchDataWithFilters(`/contracts`, params, 1, 500, readyToFetchData);

  if (!state.value || state.loading) {
    return null;
  }

  return state.value.results;
};

export const useActiveUnbalancedContractsToDate = (
  date: Date | null,
  readyToFetchData?: boolean,
  fetchMax?: boolean,
  withLimitDate?: boolean,
): SortingCenterContract[] | null => {
  let params: QueryParams = {};
  if (date && isValidDate(date)) {
    params['startDate[before]'] = date.toDateString();
    params['isBalanced'] = '0';
    params['exists[dateFermetureContrat]'] = 'false';
  }

  if (withLimitDate) {
    params['withLimitDate'] = '1';
  }

  // Maximum de contrat a 300 sinon c'est trop lourd a afficher.
  // Ce problème doit etre résolue avec le ticket ET-2197
  const itemsPerPage = fetchMax ? 300 : 50;

  const state = useFetchDataWithFilters('/contracts', params, 1, itemsPerPage, readyToFetchData);

  if (!state.value || state.loading) {
    return null;
  }

  return state.value.results;
};

export const useContractsClosedThisYear = (
  year: number | null,
  readyToFetchData?: boolean,
  fetchMax?: boolean,
): SortingCenterContract[] | null => {
  let params: QueryParams = {};
  readyToFetchData = readyToFetchData && null !== year;

  if (null !== year) {
    const startOfYear = new Date();
    startOfYear.setFullYear(year);
    startOfYear.setMonth(0);
    startOfYear.setDate(1);
    startOfYear.setHours(0);
    startOfYear.setMinutes(0);
    startOfYear.setSeconds(0);
    params['dateFermetureContrat[after]'] = startOfYear.toDateString();
  }

  // Maximum de contrat a 300 sinon c'est trop lourd a afficher.
  // Ce problème doit etre résolue avec le ticket ET-2197
  const itemsPerPage = fetchMax ? 300 : 50;

  const state = useFetchDataWithFilters('/contracts', params, 1, itemsPerPage, readyToFetchData);

  if (!state.value || state.loading) {
    return null;
  }

  return state.value.results;
};

export const useClosedClosures = (): [Closure[] | null, number] => {
  const state = useFetchDataWithFilters(`/closures`, { status: ClosureStatusEnum.CLOSED }, 1, 120);

  if (!state.value || state.loading) {
    return [null, -1];
  }

  return [state.value.results, state.value.total];
};

export const useLastClosedOrReopenClosure = (): Closure | false => {
  const queryParams = new URLSearchParams();
  queryParams.append('status[]', ClosureStatusEnum.CLOSED);
  queryParams.append('status[]', ClosureStatusEnum.REOPEN);
  queryParams.append('order[year]', 'DESC');
  queryParams.append('order[month]', 'DESC');

  const queryString = queryParams.toString();

  const state = useFetchDataWithFilters(`/closures?${queryString}`, {}, 1, 1);

  if (!state.value || state.loading) {
    return false;
  }

  if (state.value.total > 0) {
    return state.value.results?.[0];
  }

  return false;
};

export const useClosureById = (id: string): [Closure | null, boolean] => {
  const state = useAsync(async () => {
    return await client.get(`/closures/${id}`);
  });

  return [state.value, state.loading];
};

export const useFirstClosure = (readyToFetchData: boolean = true): Closure | null => {
  const state = useFetchDataWithFilters(
    `/closures`,
    { 'order[year]': 'ASC', 'order[month]': 'ASC' },
    1,
    1,
    readyToFetchData,
    Event.REFRESH_CLOSURES,
  );
  if (!state.value || state.loading) {
    return null;
  }

  return state.value.results?.[0];
};

export const useClosures = (year: undefined | string | number): [Closure[] | null, number] => {
  const state = useFetchDataWithFilters(
    `/closures`,
    year ? { year: year.toString() } : undefined,
    1,
    120,
    Boolean(year),
    Event.REFRESH_CLOSURES,
  );
  if (!state.value || state.loading) {
    return [null, -1];
  }

  return [state.value.results, state.value.total];
};

export const useClosureByMonthAndYear = (
  year: undefined | string | number,
  month: undefined | string | number,
  sortingCenterId: string | undefined,
): Closure | null => {
  const state = useFetchDataWithFilters(
    `/closures`,
    year && month && sortingCenterId
      ? { year: String(year), month: String(month), sortingCenterId: sortingCenterId }
      : undefined,
    1,
    120,
    Boolean(year),
    Event.REFRESH_CLOSURES,
  );
  if (!state.value || state.loading || state.value.total !== 1) {
    return null;
  }

  return state.value.results[0];
};

export const useSortingCenterContractsForUserId = (
  sortingCenterId: string | undefined,
  userId: number,
): AsyncState<SortingCenterContract[] | null> => {
  return useAsync(async () => {
    if (!sortingCenterId) return null;

    return client.get(`/sorting_centers/${sortingCenterId}/clients/${userId}/contracts`);
  }, [userId]);
};

export const useClientContracts = (
  params?: QueryParams,
  page = 1,
  itemsPerPage = 200,
  readyToFetch = true,
): [ClientContract[] | null, number] => {
  const state = useFetchDataWithFilters(`/client_contracts`, params, page, itemsPerPage, readyToFetch);

  if (!state.value || state.loading) {
    return [null, -1];
  }

  return [state.value.results, state.value.total];
};

export const useSortingCenterContractStatusesIndexedByClientIds = (
  materialId: string,
  buyerId: string,
  expeditionDate: string | null,
  page = 1,
  itemsPerPage = 200,
  readyToFetch = true,
): { [key: number]: SortingCenterContractStatus } | null => {
  const currentReadyToFetch = Boolean(readyToFetch && materialId && buyerId);
  const [finalResults, setFinalResults] = useState<{ [key: number]: SortingCenterContractStatus } | null>(null);
  const [sortingCenterContractStatuses, , isLoading] = useSortingCenterContractStatuses(
    materialId,
    buyerId,
    expeditionDate,
    page,
    itemsPerPage,
    currentReadyToFetch,
  );

  useEffect(() => {
    if (null !== sortingCenterContractStatuses && !isLoading) {
      const resultsIndexedByClientIds: { [key: number]: SortingCenterContractStatus } = {};

      for (const sortingCenterContractStatus of sortingCenterContractStatuses) {
        resultsIndexedByClientIds[sortingCenterContractStatus.clientId] = sortingCenterContractStatus.status;
      }

      setFinalResults(resultsIndexedByClientIds);
    }
  }, [sortingCenterContractStatuses, isLoading]);

  return finalResults;
};

export const useSortingCenterContractStatuses = (
  materialId: string,
  buyerId: string,
  expeditionDate: string | null,
  page = 1,
  itemsPerPage = 200,
  readyToFetch = true,
): [SortingCenterContractStatusByClient[] | null, number, boolean] => {
  const params: {
    materialId: string;
    buyerId: string;
    expeditionDate: string;
  } = {
    materialId,
    buyerId,
    expeditionDate: expeditionDate ?? '',
  };

  const state = useFetchDataWithFilters(`/sorting_center_contract_statuses`, params, page, itemsPerPage, readyToFetch);

  if (state.loading) {
    return [null, -1, true];
  } else if (!state.value) {
    return [null, 0, false];
  }

  return [state.value.results, state.value.total, false];
};

export const useMaterials = (params?: QueryParams): Material[] | null => {
  const state = useFetchDataWithFilters(`/materials`, params, undefined, undefined, undefined, Event.REFRESH_MATERIAL);

  if (!state.value || state.loading) {
    return null;
  }

  return state.value.results;
};

export const useClients = (itemsPerPage = 50): [Client[] | null, number] => {
  const state = useFetchDataWithFilters(`/clients`, {}, 1, itemsPerPage);

  if (!state.value || state.loading) {
    return [null, -1];
  }

  return [state.value.results, state.value.total];
};

export const useClientContractsForClientAndBuyer = (
  clientId: string,
  buyerId: string,
): [ClientContract[] | null, number] => {
  let params: QueryParams = {};

  params['client'] = clientId;
  params['buyer'] = buyerId;

  const state = useFetchDataWithFilters(
    `/client_contracts`,
    params,
    undefined,
    undefined,
    true,
    Event.REFRESH_CLIENT_CONTRACTS,
  );

  if (!state.value || state.loading) {
    return [null, -1];
  }

  return [state.value.results, state.value.total];
};

export const useMaterialsWithClientContract = (contracts: ClientContract[] | null): Material[] => {
  const [materialChoices, setMaterialChoices] = useState<Material[]>([]);

  useEffect(() => {
    setMaterialChoices(getMaterialsFromClientContracts(contracts));
  }, [contracts]);

  return materialChoices;
};

export const useBuyers = (params?: QueryParams): [Buyer[] | null, number] => {
  const state = useFetchDataWithFilters(`/buyers`, params);

  if (!state.value || state.loading) {
    return [null, -1];
  }

  return [state.value.results, state.value.total];
};

export const useDistinctRepartitionStatus = (): [[boolean | null] | null, number] => {
  const state = useFetchDataWithFilters(`/get_distinct_repartition_status`);

  if (!state.value || state.loading) {
    return [null, -1];
  }

  return [state.value.results, state.value.total];
};

export const useBuyersFromMaterialWithClientContract = (
  materialId: string | undefined | null,
  contracts: ClientContract[] | null,
): Buyer[] => {
  const [streamChoices, setBuyerChoices] = useState<Buyer[]>([]);

  useEffect(() => {
    setBuyerChoices(getBuyersFromMaterialWithClientContracts(materialId, contracts));
  }, [materialId, contracts]);

  return streamChoices;
};

export const useMaterialStandards = (): AsyncState<MaterialStandard[] | null> => {
  return useAsync(async () => {
    return await client.get(`/material_standards`);
  }, []);
};

export const useActiveBuyersForClient = (
  sortingCenterId: string | undefined,
  clientId: number | null,
): AsyncState<Buyer[] | null> => {
  return useAsync(async () => {
    if (!sortingCenterId) return null;
    if (!clientId) return null;

    return await client.get(`/sorting_centers/${sortingCenterId}/clients/${clientId}/buyers`);
  }, [sortingCenterId, clientId]);
};

export const useClientsFromMaterialAndBuyerWithClientContract = (
  materialId: string | undefined | null,
  buyerId: string | undefined | null,
  contracts: ClientContract[] | null,
): Client[] => {
  const [clientsList, setClientsList] = useState<Client[]>([]);

  useEffect(() => {
    setClientsList(getClientsForGivenBuyerAndMaterialFromContracts(materialId, buyerId, contracts));
  }, [materialId, buyerId, contracts]);

  return clientsList;
};

export const useClientsWithActiveContractToDate = (activeContracts: SortingCenterContract[] | null): Client[] => {
  const [clientChoices, setClientChoices] = useState<Client[]>([]);

  useEffect(() => {
    setClientChoices(getClientsFromActiveContracts(activeContracts));
  }, [activeContracts]);

  return clientChoices;
};

export const useClientsWithActiveContractAndContractsClosedThisYear = (
  activeContracts: SortingCenterContract[] | null,
  contractsClosedThisYear: SortingCenterContract[] | null,
): Client[] => {
  const [clientChoices, setClientChoices] = useState<Client[]>([]);

  useEffect(() => {
    const currentActiveContracts = activeContracts ?? [];
    const currentContractsClosedThisYear = contractsClosedThisYear ?? [];
    setClientChoices(getClientsFromActiveContracts([...currentActiveContracts, ...currentContractsClosedThisYear]));
  }, [activeContracts, contractsClosedThisYear]);

  return clientChoices;
};

export const useStreamsWithClientAndActiveContractToDate = (
  clientId: string | undefined | null,
  contracts: SortingCenterContract[] | null,
): Stream[] => {
  const [streamChoices, setStreamChoices] = useState<Stream[]>([]);

  useEffect(() => {
    setStreamChoices(getStreamsFromClientAndActiveContracts(clientId, contracts));
  }, [clientId, contracts]);

  return streamChoices;
};

export const useStreams = (itemsPerPage = 50, filters: StreamFilters = {}): [Stream[] | null, number] => {
  const params: QueryParams = {
    isActiveStream: String(filters?.isActiveStream),
    sortingCenterId: String(filters?.sortingCenterId),
    year: String(filters?.year),
  };
  const state = useFetchDataWithFilters(`/streams`, params, undefined, itemsPerPage, undefined, Event.REFRESH_STREAMS);

  if (!state.value || state.loading) {
    return [null, -1];
  }

  return [state.value.results, state.value.total];
};

export const useEligibleStreams = (clientId: string | undefined | null): [Stream[] | null, number] => {
  const state = useFetchDataWithFilters(
    `/streams/${clientId}/eligible`,
    {},
    undefined,
    undefined,
    true,
    Event.REFRESH_STREAMS,
  );

  if (!state.value || state.loading) {
    return [null, -1];
  }

  return [state.value.results, state.value.total];
};

export const useInformationMessage = (
  messageType: InformationMessageType = InformationMessageType.BOTH,
): AsyncState<InformationMessage[]> => {
  const today = new Date().toISOString();
  const stringParams = new URLSearchParams();
  stringParams.append('startDate[before]', today);
  stringParams.append('endDate[after]', today);

  if (messageType !== InformationMessageType.BOTH) {
    stringParams.append('type[exact]', messageType);
  }

  let path = `/information_messages`;
  if (!!stringParams.toString()) {
    path = path + '?' + stringParams;
  }
  return useAsync(async () => {
    return await client.get(path);
  });
};
export const useChangelog = (
  rightBoundaryDate?: Date | undefined,
  leftBoundaryDate?: Date | undefined,
): AsyncState<ChangeLog[]> => {
  const stringParams = new URLSearchParams();
  if (rightBoundaryDate) {
    stringParams.append('releaseDate[before]', rightBoundaryDate.toDateString());
  }
  if (leftBoundaryDate) {
    stringParams.append('releaseDate[after]', leftBoundaryDate.toDateString());
  }
  let path = `/change_logs`;
  if (!!stringParams.toString()) {
    path = path + '?' + stringParams;
  }
  return useAsync(async () => {
    return await client.get(path, { checkToken: false });
  });
};

export const useFetchDataWithFilters = (
  apiPath: string,
  params: QueryParams = {},
  page: number = 1,
  itemsPerPage: number = 50,
  readyToFetchData: boolean = true,
  eventSubscribed: Event | null = null,
): AsyncState<{
  results: any;
  total: any;
}> => {
  const context = useContext(AppContext);
  const sortingCenterId = transformObjectId(context.getSortingCenter?.()) || params.sortingCenterId;

  const [state, fetch] = useAsyncFn(async () => {
    if (!sortingCenterId || !readyToFetchData) return { results: null, total: null };

    const stringParams = new URLSearchParams(params as Record<string, string>);
    stringParams.append('page', page.toString());

    if (!params['itemsPerPage']) {
      stringParams.append('itemsPerPage', itemsPerPage.toString());
    }
    let path = `/sorting_centers/${sortingCenterId}${apiPath}`;
    if (!!stringParams.toString()) {
      path = path + '?' + stringParams;
    }

    const result = await client.get(path, {
      fullbody: true,
    });

    return {
      results: result['hydra:member'],
      total: result['hydra:totalItems'],
    };
  }, [page, itemsPerPage, readyToFetchData, JSON.stringify(params), sortingCenterId]);

  useEffect(() => {
    fetch();
    if (eventSubscribed) {
      const sub = PubSub.subscribe(eventSubscribed, () => fetch());
      return () => {
        PubSub.unsubscribe(sub);
      };
    }
  }, [page, itemsPerPage, fetch, eventSubscribed]);

  return state;
};

export const useReceptionsWithFilter = (
  pagination: Pagination | null = null,
  order?: OrderFilter,
  filter?: ReceptionFilters | null,
): [Reception[] | null, number] => {
  let params: QueryParams = formatFilterAsQueryParams(filter, 'receptionDatetime');
  if (order?.orderBy) {
    params[`order[${order.orderBy}]`] = order.order;
  }

  const state = useFetchDataWithFilters(
    '/receptions',
    params,
    pagination?.page,
    pagination?.itemsPerPage,
    true,
    Event.REFRESH_RECEPTIONS,
  );

  if (!state.value || state.loading) {
    return [null, -1];
  }

  return [state.value.results, state.value.total];
};

export const useReceptionById = (id: string | null): AsyncState<Reception | null> => {
  return useAsync(async () => {
    if (!id) return null;

    return await client.get(`/receptions/${id}`);
  }, [id]);
};
export const useProductionById = (id: string | null): AsyncState<Production | null> => {
  return useAsync(async () => {
    if (!id) return null;

    return await client.get(`/productions/${id}`);
  }, [id]);
};
export const useExpeditionById = (id: string | null): AsyncState<Expedition | null> => {
  const [state, fetch] = useAsyncFn(async () => {
    if (!id) return null;

    return await client.get(`/expeditions/${id}`);
  }, [id]);

  useEffect(() => {
    fetch();
    const sub = PubSub.subscribe(Event.REFRESH_EXPEDITION_BUYER_CONFIRMATION, () => fetch());
    return () => {
      PubSub.unsubscribe(sub);
    };
  }, [fetch]);
  return state;
};
export const useExpeditionBuyerConfirmationByExpeditionId = (
  expeditionId: string | null,
): AsyncState<[ExpeditionBuyerConfirmation | null]> => {
  const [state, fetch] = useAsyncFn(async () => {
    if (!expeditionId) return null;
    const filterParam = `expedition=${expeditionId}`;
    return await client.get(`/expedition_buyer_confirmations?${filterParam}`);
  }, [expeditionId]);

  useEffect(() => {
    fetch();
    const sub = PubSub.subscribe(Event.REFRESH_EXPEDITION_BUYER_CONFIRMATION, () => fetch());
    return () => {
      PubSub.unsubscribe(sub);
    };
  }, [fetch]);
  return state;
};
export const useClientById = (id: string | null): AsyncState<Client | null> => {
  return useAsync(async () => {
    if (!id) return null;

    return await client.get(`/clients/${id}`);
  }, [id]);
};
export const useBuyerById = (id: string | null): AsyncState<Buyer | null> => {
  const context = useContext(AppContext);
  const sortingCenter = context.getSortingCenter?.();
  const sortingCenterId = transformObjectId(sortingCenter);

  const [state, fetch] = useAsyncFn(async () => {
    if (!id) return null;

    return await client.get(`/buyers/${id}?sortingCenterId=${sortingCenterId}`);
  }, [id]);

  useEffect(() => {
    fetch();
    const sub = PubSub.subscribe(Event.REFRESH_BUYERS, () => fetch());
    return () => {
      PubSub.unsubscribe(sub);
    };
  }, [fetch]);

  return state;
};
export const useSortingCenterById = (id: string | null): AsyncState<SortingCenter | null> => {
  const [state, fetch] = useAsyncFn(async () => {
    if (!id) return null;

    return await client.get(`/sorting_centers/${id}`);
  }, [id]);

  useEffect(() => {
    fetch();
    const sub = PubSub.subscribe(Event.REFRESH_SORTING_CENTER, () => fetch());
    return () => {
      PubSub.unsubscribe(sub);
    };
  }, [fetch]);

  return state;
};

export const useEstimatedReceptionsWithFilter = (
  month: number | null,
  year: number | null,
): EstimatedReception[] | null => {
  const params: QueryParams = { month: month?.toString() ?? '', year: year?.toString() ?? '' };
  const state = useFetchDataWithFilters(
    '/estimated_receptions',
    params,
    undefined,
    undefined,
    Boolean(month && year),
    Event.REFRESH_ESTIMATED_RECEPTIONS,
  );

  if (!state.value || state.loading) {
    return null;
  }

  return state.value.results;
};

export const useStockConditionneRepreneurWithFilter = (
  filters?: StockConditionneRepreneurFilters,
): StockConditionneRepreneurByBuyerThenMaterial | null => {
  let params: QueryParams = {};

  if (filters?.buyerId) {
    params['buyerId'] = filters.buyerId;
  }

  if (filters?.materialId) {
    params['materialId'] = filters.materialId;
  }

  const state = useFetchDataWithFilters('/buyers/stock_conditionne', params, 1, DEFAULT_ITEMS_PER_SELECT);

  if (!state.value || state.loading) {
    return null;
  }

  return state.value.results;
};

export const useCharacterizationById = (id: string | null): AsyncState<Characterization | null> => {
  return useAsync(async () => {
    if (!id) return null;

    return await client.get(`/characterizations/${id}`);
  }, [id]);
};

export const useCharacterizationsWithFilter = (
  pagination: Pagination | null = null,
  order?: OrderFilter,
  filter?: CharacterizationFilters | null,
): [Characterization[] | null, number] => {
  let params: QueryParams = formatFilterAsQueryParams(filter, 'samplingDate');
  if (order?.orderBy) {
    params[`order[${order.orderBy}]`] = order.order;
  }

  if (filter?.hasMultipleCharacterizations) {
    params['hasMultipleCharacterizations'] = String(filter?.hasMultipleCharacterizations);
  }

  const state = useFetchDataWithFilters(
    '/characterizations',
    params,
    pagination?.page,
    pagination?.itemsPerPage,
    true,
    Event.REFRESH_CHARACTERIZATIONS,
  );

  if (!state.value || state.loading) {
    return [null, -1];
  }

  return [state.value.results, state.value.total];
};

export const useAverageBallWeightByMaterialId = (
  year: string,
  month: string,
): [CalculatedAverageBallWeightByMaterialId | null, boolean] => {
  const context = useContext(AppContext);
  const sortingCenter = context.getSortingCenter?.();
  const sortingCenterId = transformObjectId(sortingCenter);
  const readyToFetch = Boolean(sortingCenterId);

  const state = useFetchDataWithFilters(`/average_ball_weight/${year}-${month}`, {}, 1, 50, readyToFetch);

  if (!state.value?.results || state.loading) {
    return [null, true];
  }

  const averageBallWeightApi: AverageBallWeight[] = state.value?.results;

  const averageBallWeight = averageBallWeightApi.reduce<CalculatedAverageBallWeightByMaterialId>(
    (averageBallWeightByMaterialId, averageBallWeight) => {
      averageBallWeightByMaterialId[averageBallWeight.materialId] = averageBallWeight.averageBallWeight;
      return averageBallWeightByMaterialId;
    },
    {},
  );

  return [averageBallWeight, false];
};

export const useExpeditionsWithFilter = (
  pagination: Pagination | null = null,
  order?: OrderFilter,
  filters?: ExpeditionFilters,
  strictBefore: boolean = true,
): [Expedition[] | null, number] => {
  let params: QueryParams = formatFilterAsQueryParams(filters, 'expeditionDate', strictBefore);
  if (order?.orderBy) {
    params[`order[${order.orderBy}]`] = order.order;
  }

  if (filters?.invalidOnly) {
    params['exists[expeditionBuyerConfirmation]'] = 'false';
  }

  if (undefined !== filters?.isMiseEnCoherenceAccepted) {
    if (null === filters.isMiseEnCoherenceAccepted) {
      params['exists[expeditionBuyerConfirmation]'] = 'true';
      params['exists[isMiseEnCoherenceAccepted]'] = 'false';
    } else {
      params['isMiseEnCoherenceAccepted'] = filters.isMiseEnCoherenceAccepted.toString();
    }
  }

  const state = useFetchDataWithFilters(
    '/expeditions',
    params,
    pagination?.page,
    pagination?.itemsPerPage,
    true,
    Event.REFRESH_EXPEDITIONS,
  );

  if (!state.value || state.loading) {
    return [null, -1];
  }

  return [state.value.results, state.value.total];
};

export const useProductionsWithFilter = (
  pagination: Pagination | null = null,
  order?: OrderFilter,
  filters?: ProductionFilters,
  strictBefore: boolean = true,
): [Production[] | null, number] => {
  let params: QueryParams = formatFilterAsQueryParams(filters, 'productionDate', strictBefore);
  if (order?.orderBy) {
    params[`order[${order.orderBy}]`] = order.order;
  }

  const state = useFetchDataWithFilters(
    '/productions',
    params,
    pagination?.page,
    pagination?.itemsPerPage,
    true,
    Event.REFRESH_PRODUCTIONS,
  );

  if (!state.value || state.loading) {
    return [null, -1];
  }

  return [state.value.results, state.value.total];
};

export const useClientsWithFilter = (
  pagination: Pagination | null = null,
  order?: OrderFilter,
): [Client[] | null, number] => {
  let params: QueryParams = {};
  if (order?.orderBy) {
    params[`order[${order.orderBy}]`] = order.order;
  }

  const state = useFetchDataWithFilters(
    '/clients',
    params,
    pagination?.page,
    pagination?.itemsPerPage,
    true,
    Event.REFRESH_CLIENTS,
  );

  if (!state.value || state.loading) {
    return [null, -1];
  }

  return [state.value.results, state.value.total];
};

export const useBuyersWithFilter = (
  pagination: Pagination | null = null,
  order?: OrderFilter,
): [Buyer[] | null, number] => {
  let params: QueryParams = {};
  if (order?.orderBy) {
    params[`order[${order.orderBy}]`] = order.order;
  }

  const state = useFetchDataWithFilters(
    '/buyers',
    params,
    pagination?.page,
    pagination?.itemsPerPage,
    true,
    Event.REFRESH_BUYERS,
  );

  if (!state.value || state.loading) {
    return [null, -1];
  }

  return [state.value.results, state.value.total];
};

export const useGetLatestStockModificationDate = () => {
  const today = new Date();
  const [latestDate, setLatestDate] = useState<Date | null>(null);

  const [latestClosure, isLoadingLatestClosure] = useLatestClosure();

  const [expeditionList, totalExpeditions] = useExpeditionsWithFilter(
    { page: 1, itemsPerPage: 1 },
    { order: 'desc', orderBy: 'expeditionDate' },
    { endDate: today },
  );

  const [productionList, totalProductions] = useProductionsWithFilter(
    { page: 1, itemsPerPage: 1 },
    { order: 'desc', orderBy: 'productionDate' },
    { endDate: today },
    false,
  );

  useEffect(() => {
    if (isLoadingLatestClosure || 0 > totalExpeditions || 0 > totalProductions) return;

    const latestClosureDate = latestClosure ? new Date(latestClosure.year, latestClosure.month, 0) : null;
    const expeditionDateBeforeToday = expeditionList?.[0] ? new Date(expeditionList[0].expeditionDate) : null;
    const productionDateBeforeToday = productionList?.[0] ? new Date(productionList[0].productionDate) : null;
    const maxTime = Math.max(
      latestClosureDate?.getTime() ?? 0,
      expeditionDateBeforeToday?.getTime() ?? 0,
      productionDateBeforeToday?.getTime() ?? 0,
    );

    if (maxTime === 0) return;

    setLatestDate(new Date(maxTime));
  }, [latestClosure, expeditionList, productionList, isLoadingLatestClosure, totalExpeditions, totalProductions]);

  return latestDate;
};

export const useClosureRequirements = (
  sortingCenterId: string | undefined,
  month: number | undefined,
  year: number | undefined,
): [SortingCenterContractsRequirements | null, boolean] => {
  const [state, fetch] = useAsyncFn(async () => {
    if (!sortingCenterId || !month || !year) return null;

    const path = `/sorting_centers/${sortingCenterId}/closures/${year}-${month}/requirements`;

    const result = await client.get(path);

    return result;
  }, [sortingCenterId, month, year]);

  useEffect(() => {
    fetch();
  }, [sortingCenterId, month, year, fetch]);

  return [state.value, state.loading];
};

export const useInitialInboundStockDate = (): string | null => {
  let params: QueryParams = {
    'order[updateDateTime]': 'asc',
  };
  const path = `/inbound_stocks`;
  const state = useFetchDataWithFilters(path, params, 1, 1);

  if (!state.value || state.loading) {
    return null;
  }

  if (state.value.results.length < 1) {
    return null;
  }
  return state.value.results[0].updateDateTime;
};

export const useInboundStocks = (year: number, month: number): [InboundStock[] | null, number] => {
  const dateBefore = new Date(year, month, 1);
  const dateAfter = new Date(year, month - 1, 0);

  let params: QueryParams = {
    'updateDateTime[strictly_before]': formatDateAsQueryParam(dateBefore),
    'updateDateTime[strictly_after]': formatDateAsQueryParam(dateAfter),
  };
  const path = `/inbound_stocks`;
  const state = useFetchDataWithFilters(path, params, 1, DEFAULT_ITEMS_PER_SELECT);

  if (!state.value || state.loading) {
    return [null, -1];
  }

  return [state.value.results, state.value.total];
};

export const useInventoriedInboundStocks = (
  year: string,
  month: string,
  itemsPerPage?: number,
): [InventoriedInboundStock[] | null, number] => {
  let params: QueryParams = {
    year,
    month,
    'order[stream.name]': 'ASC',
  };
  const path = `/inventoried_inbound_stocks`;
  const state = useFetchDataWithFilters(
    path,
    params,
    undefined,
    itemsPerPage,
    undefined,
    Event.REFRESH_INVENTORIED_INBOUND_STOCKS,
  );

  if (!state.value || state.loading) {
    return [null, -1];
  }

  return [state.value.results, state.value.total];
};

export const useLatestInboundStock = (): [InboundStock | null, boolean] => {
  let params: QueryParams = {
    'order[updateDateTime]': 'desc',
  };
  const path = `/inbound_stocks`;
  const state = useFetchDataWithFilters(path, params, 1, 1);

  if (state.loading) {
    return [null, false];
  }

  if (!state.value || state.value.results.length < 1) {
    return [null, true];
  }
  return [state.value.results[0], true];
};

export const useYearlyWastages = (): [Wastage[] | null, boolean] => {
  const path = `/wastages/yearly`;
  const state = useFetchDataWithFilters(path);

  if (state.loading) {
    return [null, false];
  }

  if (!state.value) {
    return [null, true];
  }
  return [state.value.results, true];
};

export const useSortingCenterContractsById = (id: string | null): AsyncState<SortingCenterContract | null> => {
  return useAsync(async () => {
    if (!id) return null;

    return await client.get(`/sorting_center_contracts/${id}`);
  }, [id]);
};

export const useEstimatedReceptionBySortingCenterContract = (
  id: string | null | undefined,
): AsyncState<EstimatedReception | null> => {
  return useAsync(async () => {
    if (!id) return null;

    return await client.get(`/sorting_center_contracts/${id}/estimated_reception`);
  }, [id]);
};

export const useFetchAdminDataWithFilters = (
  apiPath: string,
  params: QueryParams = {},
  page: number = 1,
  itemsPerPage: number = 50,
  readyToFetchData: boolean = true,
): AsyncState<{
  results: any;
  total: any;
}> => {
  const [state, fetch] = useAsyncFn(async () => {
    if (!readyToFetchData) return { results: null, total: null };

    const stringParams = new URLSearchParams(params as Record<string, string>);
    //stringParams.append('page', page.toString());
    //stringParams.append('itemsPerPage', itemsPerPage.toString());

    let path = `/admin/${apiPath}`;
    if (!!stringParams.toString()) {
      path = path + '?' + stringParams;
    }

    const result = await client.get(path, {
      fullbody: true,
    });

    return {
      results: result['hydra:member'],
      total: result['hydra:totalItems'],
    };
  }, [page, itemsPerPage, readyToFetchData, JSON.stringify(params)]);

  useEffect(() => {
    fetch();
  }, [page, itemsPerPage, fetch]);

  return state;
};

export const useUsersWithFilter = (
  pagination: Pagination | null = null,
  order?: OrderFilter,
  filter?: null,
): [User[] | null, number] => {
  let params: QueryParams = {};

  const state = useFetchAdminDataWithFilters('users', params, pagination?.page, pagination?.itemsPerPage, true);

  if (!state.value || state.loading) {
    return [null, -1];
  }

  return [state.value.results, state.value.total];
};

export const useUserById = (id: string | null): User | null => {
  const [user, setUser] = useState<User>();
  const [state, fetch] = useAsyncFn(async () => {
    return await client.get(`/admin/users/${id}`);
  }, []);

  useEffect(() => {
    fetch().then(setUser);
    const sub = PubSub.subscribe(Event.REFRESH_ADMIN_USER, () => fetch().then(setUser));

    return () => {
      PubSub.unsubscribe(sub);
    };
  }, [fetch]);

  if (!user || state.loading) {
    return null;
  }

  return user;
};
